import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Heading,
    LabeledContainer,
    LabeledContainerBody,
    LabeledContainerHeader,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    type ListRenderItemInfo,
    ListSkeleton,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { UserAvatar } from '../Users';

type ChangeRequestParticipantsListProps = {
    changeRequest: api.ChangeRequest;
};

/**
 * List the participants for a change request.
 */
export function ChangeRequestParticipantsList(props: ChangeRequestParticipantsListProps) {
    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <Heading>Participants</Heading>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <React.Suspense fallback={<ListSkeleton compact />}>
                    <ChangeRequestParticipantsListBody {...props} />
                </React.Suspense>
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

function ChangeRequestParticipantsListBody(props: { changeRequest: api.ChangeRequest }) {
    const { changeRequest } = props;

    const { data: edits } = useSuspenseQuery(
        apiQuery('getContributorsByChangeRequestId', {
            spaceId: changeRequest.space,
            changeRequestId: changeRequest.id,
        })
    );

    const sortedItems = React.useMemo(() => {
        const creatorId = changeRequest.createdBy.id;
        return [...edits.items].sort((a, b) => {
            const aIsCreator = a.user.id === creatorId;
            const bIsCreator = b.user.id === creatorId;

            // Creator always comes first
            if (aIsCreator && !bIsCreator) return -1;
            if (!aIsCreator && bIsCreator) return 1;

            // Otherwise sort by count
            return a.count - b.count;
        });
    }, [edits.items, changeRequest.createdBy.id]);

    return (
        <List
            items={sortedItems}
            renderItem={({ item }: ListRenderItemInfo<api.ChangeRequestUserContributor>) => (
                <ListItem compact>
                    <ListItemIndicator>
                        <UserAvatar size="md" tooltip={false} user={item.user} className="mr-1" />
                    </ListItemIndicator>
                    <ListItemBody className="flex flex-col gap-0.5">
                        <ListItemDescription className="flex items-center gap-2">
                            <span className="truncate">{item.user.displayName}</span>
                            {item.user.id === changeRequest.createdBy.id ? (
                                <Badge kind="secondary" size="xsmall" label="Creator" />
                            ) : null}
                        </ListItemDescription>
                        <ListItemDetail>
                            {ACTION_TYPE_LABELS[item.actionType]}{' '}
                            <DateShortRelative value={item.updatedAt} onlyDate={false} />
                        </ListItemDetail>
                    </ListItemBody>
                    {item.actionType === 'comment' ? (
                        <ListItemControls when="always">
                            <Button
                                kind="blank"
                                leadingIcon={Icon.Comment}
                                label="View comments"
                                iconOnly
                                href={{
                                    screen: {
                                        sideSheet: {
                                            type: 'comments',
                                        },
                                    },
                                }}
                            />
                        </ListItemControls>
                    ) : null}
                </ListItem>
            )}
        />
    );
}

const ACTION_TYPE_LABELS: Record<api.ChangeRequestUserContributor['actionType'], string> = {
    comment: 'Commented',
    edit: 'Last edited',
};
